/* Collection grid: filters, sort, the mobile overlay, price range, Load more.
 *
 * Everything is delegated off the section root ([data-collection-root]), so
 * swapping the inner regions never orphans a handler.
 *
 * Filter, sort and clear are real links (url_to_add / url_to_remove). A click
 * fetches that URL as a full page, then replaces the desktop bar, the drawer
 * body and the results in place, and pushes the URL. A full page rather than
 * the Section Rendering API because Noted 2.0 measured the API returning
 * nothing for a template's main section under `shopify theme dev`. The mobile
 * overlay stays open across the swap, with its open groups and scroll kept.
 *
 * Load more fetches `paginate.next.url` and appends the new page's items.
 * Placed cards arrive with their own page, because positions are absolute.
 *
 * Quick add on the cards is delegated in product-card.js, so swapped and
 * appended cards need nothing here.
 */
(function () {
  /* Page scroll lock, shared with the other overlays (assets/scroll-lock.js). */
  function lockPage(on) {
    if (window.DDJScrollLock) window.DDJScrollLock.set('filters', on);
    else document.body.style.overflow = on ? 'hidden' : '';
  }

  if (window.__ddjCollection) return;
  window.__ddjCollection = true;

  function fetchDoc(url) {
    return fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(function (r) {
        if (!r.ok) throw new Error('Request failed: ' + r.status);
        return r.text();
      })
      .then(function (html) {
        return new DOMParser().parseFromString(html, 'text/html');
      });
  }

  function swapInner(root, doc, selector) {
    var from = doc.querySelector('[data-collection-root] ' + selector);
    var to = root.querySelector(selector);
    if (from && to) to.innerHTML = from.innerHTML;
  }

  function activeTotal(root) {
    var src = root.querySelector('[data-drawer-body]') || root.querySelector('[data-filter-bar]');
    var n = src ? parseInt(src.getAttribute('data-active-total'), 10) || 0 : 0;
    root.querySelectorAll('[data-active-count]').forEach(function (el) {
      el.textContent = n > 0 ? ' (' + n + ')' : '';
    });
  }

  /* ─── Desktop panels: one open at a time ─── */
  function closePanels(root, except) {
    root.querySelectorAll('[data-facet-toggle][aria-expanded="true"]').forEach(function (btn) {
      if (btn === except) return;
      setPanel(btn, false);
    });
  }

  function setPanel(btn, open) {
    var panel = document.getElementById(btn.getAttribute('aria-controls'));
    btn.setAttribute('aria-expanded', open ? 'true' : 'false');
    var x = btn.querySelector('[data-facet-close]');
    if (x) x.classList.toggle('hidden', !open);
    if (panel) panel.hidden = !open;
  }

  /* ─── Mobile overlay ─── */
  function openDrawer(root, group) {
    var drawer = root.querySelector('[data-filter-drawer]');
    if (!drawer) return;
    root._trigger = document.activeElement;
    drawer.hidden = false;
    lockPage(true);
    requestAnimationFrame(function () {
      drawer.classList.add('is-open');
    });
    if (group) {
      var target = drawer.querySelector('[data-group="' + group + '"]');
      if (target) {
        target.open = true;
        target.scrollIntoView({ block: 'start' });
      }
    }
    var close = drawer.querySelector('[data-drawer-close]');
    if (close) close.focus();
  }

  function closeDrawer(root) {
    var drawer = root.querySelector('[data-filter-drawer]');
    if (!drawer || drawer.hidden) return;
    drawer.classList.remove('is-open');
    lockPage(false);
    setTimeout(function () {
      drawer.hidden = true;
    }, 200);
    if (root._trigger && root._trigger.focus) root._trigger.focus();
  }

  /* ─── Fetch and swap ─── */
  function navigate(root, url, push) {
    if (root._busy) return;
    root._busy = true;

    var body = root.querySelector('[data-drawer-body]');
    var scroll = body ? body.scrollTop : 0;
    var openGroups = {};
    root.querySelectorAll('[data-drawer-body] details[data-group]').forEach(function (d) {
      openGroups[d.getAttribute('data-group')] = d.open;
    });

    var results = root.querySelector('[data-results]');
    if (results) results.style.opacity = '0.5';

    fetchDoc(url)
      .then(function (doc) {
        swapInner(root, doc, '[data-filter-bar-wrap]');
        swapInner(root, doc, '[data-drawer-body-wrap]');
        swapInner(root, doc, '[data-results]');

        root.querySelectorAll('[data-drawer-body] details[data-group]').forEach(function (d) {
          var key = d.getAttribute('data-group');
          if (Object.prototype.hasOwnProperty.call(openGroups, key)) d.open = openGroups[key];
        });
        var newBody = root.querySelector('[data-drawer-body]');
        if (newBody) newBody.scrollTop = scroll;

        activeTotal(root);
        if (push !== false) window.history.pushState({ collection: true }, '', url);
      })
      .catch(function (err) {
        if (window.console) console.warn('[collection] update failed, loading the page instead', err);
        window.location.href = url;
      })
      .finally(function () {
        root._busy = false;
        var r = root.querySelector('[data-results]');
        if (r) r.style.opacity = '';
      });
  }

  /* ─── Load more ─── */
  function loadMore(root, button) {
    if (root._busy) return;
    root._busy = true;
    button.setAttribute('aria-busy', 'true');
    button.disabled = true;

    fetchDoc(button.getAttribute('data-next-url'))
      .then(function (doc) {
        var grid = root.querySelector('[data-collection-grid]');
        var fresh = doc.querySelector('[data-collection-root] [data-collection-grid]');
        var first = null;
        if (grid && fresh) {
          while (fresh.firstElementChild) {
            if (!first) first = fresh.firstElementChild;
            grid.appendChild(fresh.firstElementChild);
          }
        }
        swapInner(root, doc, '[data-load-more-region]');
        var link = first && first.querySelector('a');
        if (link) link.focus({ preventScroll: true });
      })
      .catch(function (err) {
        if (window.console) console.warn('[collection] load more failed', err);
        button.disabled = false;
        button.removeAttribute('aria-busy');
      })
      .finally(function () {
        root._busy = false;
      });
  }

  /* ─── Price range ─── */
  function priceParts(wrap) {
    return {
      min: wrap.querySelector('[data-price-min]'),
      max: wrap.querySelector('[data-price-max]'),
      label: wrap.querySelector('[data-price-label]'),
      currency: wrap.getAttribute('data-currency') || '',
      template: wrap.getAttribute('data-label-template') || ''
    };
  }

  function onPriceInput(input) {
    var wrap = input.closest('[data-price-range]');
    var p = priceParts(wrap);
    var lo = parseInt(p.min.value, 10);
    var hi = parseInt(p.max.value, 10);
    /* The thumbs never cross: the one being dragged stops at the other. */
    if (lo > hi) {
      if (input === p.min) p.min.value = hi;
      else p.max.value = lo;
      lo = parseInt(p.min.value, 10);
      hi = parseInt(p.max.value, 10);
    }
    if (p.label && p.template) {
      p.label.textContent = p.template
        .replace('[min]', p.currency + lo)
        .replace('[max]', p.currency + hi);
    }
  }

  function onPriceChange(root, input) {
    var wrap = input.closest('[data-price-range]');
    var p = priceParts(wrap);
    var top = parseInt(wrap.getAttribute('data-range-max'), 10);
    var lo = parseInt(p.min.value, 10);
    var hi = parseInt(p.max.value, 10);
    var url = new URL(window.location.href);
    url.searchParams.delete('page');
    var minName = wrap.getAttribute('data-param-min');
    var maxName = wrap.getAttribute('data-param-max');
    if (lo > 0) url.searchParams.set(minName, lo);
    else url.searchParams.delete(minName);
    if (hi < top) url.searchParams.set(maxName, hi);
    else url.searchParams.delete(maxName);
    navigate(root, url.toString());
  }

  function withSort(value) {
    var url = new URL(window.location.href);
    url.searchParams.set('sort_by', value);
    url.searchParams.delete('page');
    return url.toString();
  }

  /* ─── Wiring ─── */
  function init(root) {
    if (root.dataset.collectionReady === 'true') return;
    root.dataset.collectionReady = 'true';
    activeTotal(root);

    root.addEventListener('click', function (e) {
      var t = e.target;

      var toggle = t.closest('[data-facet-toggle]');
      if (toggle) {
        var open = toggle.getAttribute('aria-expanded') !== 'true';
        closePanels(root, toggle);
        setPanel(toggle, open);
        return;
      }

      var sortLink = t.closest('[data-sort-link]');
      if (sortLink) {
        e.preventDefault();
        closePanels(root);
        navigate(root, withSort(sortLink.getAttribute('data-sort-value')));
        return;
      }

      var filterLink = t.closest('[data-filter-link]');
      if (filterLink) {
        e.preventDefault();
        if (filterLink.getAttribute('aria-disabled') === 'true') return;
        closePanels(root);
        navigate(root, filterLink.href);
        return;
      }

      var opener = t.closest('[data-drawer-open]');
      if (opener) {
        openDrawer(root, opener.getAttribute('data-drawer-open'));
        return;
      }

      if (t.closest('[data-drawer-close]')) {
        closeDrawer(root);
        return;
      }

      var more = t.closest('[data-load-more]');
      if (more) {
        e.preventDefault();
        loadMore(root, more);
      }
    });

    root.addEventListener('input', function (e) {
      if (e.target.matches('[data-price-min], [data-price-max]')) onPriceInput(e.target);
    });

    root.addEventListener('change', function (e) {
      if (e.target.matches('[data-price-min], [data-price-max]')) onPriceChange(root, e.target);
    });

    root.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      var drawer = root.querySelector('[data-filter-drawer]');
      if (drawer && !drawer.hidden) closeDrawer(root);
      else closePanels(root);
    });
  }

  function initAll(scope) {
    (scope || document).querySelectorAll('[data-collection-root]').forEach(init);
  }

  /* A click anywhere outside the bar closes an open desktop panel. */
  document.addEventListener('click', function (e) {
    if (e.target.closest('[data-filter-bar]')) return;
    document.querySelectorAll('[data-collection-root]').forEach(function (root) {
      closePanels(root);
    });
  });

  /* The overlay is phone-only (hidden by CSS from md up). Widening past it
   * (a tablet turned to landscape) closes it, so the page is not left locked. */
  if (window.matchMedia) {
    var wide = window.matchMedia('(min-width: 768px)');
    var onWide = function (e) {
      if (!e.matches) return;
      document.querySelectorAll('[data-collection-root]').forEach(function (root) {
        var drawer = root.querySelector('[data-filter-drawer]');
        if (drawer && !drawer.hidden) closeDrawer(root);
      });
    };
    if (wide.addEventListener) wide.addEventListener('change', onWide);
    else if (wide.addListener) wide.addListener(onWide);
  }

  /* Back and forward re-run the filters the URL names. */
  window.addEventListener('popstate', function () {
    document.querySelectorAll('[data-collection-root]').forEach(function (root) {
      navigate(root, window.location.href, false);
    });
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });
})();
